import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Skeleton,
    LinearProgress,
    useTheme,
    Divider
} from '@mui/material';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SaveIcon from '@mui/icons-material/Save';
import { ImpactTabType } from '../../hooks/useImpactStats';

interface ImpactStatsCardProps {
    type: ImpactTabType;
    stats: any;
    loading: boolean;
}

const ImpactStatsCard: React.FC<ImpactStatsCardProps> = ({ type, stats, loading }) => {
    const theme = useTheme();

    // Determine color and icon based on the type
    const getTypeConfig = () => {
        switch (type) {
            case 'energy':
                return {
                    icon: <ElectricBoltIcon />,
                    color: theme.palette.warning.main,
                    title: 'Energy Impact',
                    progressColor: theme.palette.warning.main
                };
            case 'water':
                return {
                    icon: <WaterDropIcon />,
                    color: theme.palette.info.main,
                    title: 'Water Impact',
                    progressColor: theme.palette.info.main
                };
            case 'land':
                return {
                    icon: <LocalFloristIcon />,
                    color: theme.palette.success.main,
                    title: 'Land Impact',
                    progressColor: theme.palette.success.main
                };
            default:
                return {
                    icon: <ElectricBoltIcon />,
                    color: theme.palette.warning.main,
                    title: 'Energy Impact',
                    progressColor: theme.palette.warning.main
                };
        }
    };

    const { icon, color, title, progressColor } = getTypeConfig();

    // Generate display data based on the type
    const getDisplayData = () => {
        if (!stats || loading) return [];

        switch (type) {
            case 'energy':
                return [
                    {
                        label: 'CO₂ Reduction',
                        value: `${stats.co2Reduction} tons/year`,
                        icon: <TrendingUpIcon />,
                        progress: stats.co2Reduction / 5 * 100 // Assuming 5 tons is max
                    },
                    {
                        label: 'Renewable Adoption',
                        value: `${stats.renewableAdoption}%`,
                        icon: <ElectricBoltIcon />,
                        progress: stats.renewableAdoption
                    },
                    {
                        label: 'Cost Savings',
                        value: `$${stats.costSavings.toLocaleString()}/year`,
                        icon: <SaveIcon />,
                        progress: stats.costSavings / 2000 * 100 // Assuming $2000 is max
                    }
                ];
            case 'water':
                return [
                    {
                        label: 'Water Saved',
                        value: `${(stats.waterSaved / 1000).toLocaleString()}k gallons/year`,
                        icon: <WaterDropIcon />,
                        progress: stats.waterSaved / 30000 * 100 // Assuming 30k gallons is max
                    },
                    {
                        label: 'Quality Improvement',
                        value: `${stats.qualityImprovement}%`,
                        icon: <TrendingUpIcon />,
                        progress: stats.qualityImprovement
                    },
                    {
                        label: 'Conservation Efforts',
                        value: `${stats.conservationEfforts} initiatives`,
                        icon: <SaveIcon />,
                        progress: stats.conservationEfforts / 10 * 100 // Assuming 10 initiatives is max
                    }
                ];
            case 'land':
                return [
                    {
                        label: 'Land Restored',
                        value: `${stats.landRestored} acres`,
                        icon: <LocalFloristIcon />,
                        progress: stats.landRestored / 150 * 100 // Assuming 150 acres is max
                    },
                    {
                        label: 'Biodiversity Increase',
                        value: `${stats.biodiversityIncrease}%`,
                        icon: <TrendingUpIcon />,
                        progress: stats.biodiversityIncrease
                    },
                    {
                        label: 'Sustainable Farming',
                        value: `${stats.sustainableFarming} farms`,
                        icon: <SaveIcon />,
                        progress: stats.sustainableFarming / 50 * 100 // Assuming 50 farms is max
                    }
                ];
            default:
                return [];
        }
    };

    const displayData = getDisplayData();

    return (
        <Card
            sx={{
                backgroundColor: 'rgba(22, 28, 36, 0.7)',
                backdropFilter: 'blur(8px)',
                borderRadius: '0 0 10px 10px',
                boxShadow: 'none',
                pointerEvents: 'auto',
                overflow: 'hidden',
                borderTop: `3px solid ${color}`
            }}
        >
            <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                        sx={{
                            mr: 1.5,
                            color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography variant="h6" color="text.primary" fontWeight={600}>
                        {title} Statistics
                    </Typography>
                </Box>

                <Divider sx={{ mb: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

                {loading ? (
                    <Box sx={{ pt: 1 }}>
                        {[1, 2, 3].map((item) => (
                            <Box key={item} sx={{ mb: 2 }}>
                                <Skeleton variant="text" width="40%" height={24} />
                                <Skeleton variant="text" width="70%" height={20} />
                                <Skeleton variant="rectangular" width="100%" height={8} sx={{ mt: 1 }} />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {displayData.map((item, index) => (
                            <Box key={index} sx={{ mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box
                                            sx={{
                                                mr: 1,
                                                color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            {item.icon}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.label}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.primary" fontWeight={600}>
                                        {item.value}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(item.progress, 100)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 1,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: progressColor
                                        }
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default ImpactStatsCard; 